import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import { syncAnalyticsIdentityToDataLayer } from '../utils/gtm';
import { trackUserContextLoaded } from '../utils/frontendTrackingEvents';
import {
  applyRefreshedJwtFromResponse,
  clearAllClientAuth,
  consumeAccountDeletedIfPresent,
  ACCOUNT_DELETED_LOGOUT_EVENT,
  getAuthHeaders,
  getJwt,
  getSessionToken,
  getUserId,
  getVerifySessionUrl,
  isLogoutInProgress,
  setUserId,
  stripJwtFromSessionPayload,
} from '../utils/authHeaders';
import { allowsOnlinePresenceShare, normalizeSessionPayload } from '../utils/sessionUser';
import { tryClaimBullionLinkInviteFromStorage } from '../utils/bullionPrivateContestInvite';
import { tryClaimSquaresLinkInviteFromStorage } from '../utils/squaresLinkInvite';
import { tryClaimPendingFavouriteTeamFromStorage } from '../utils/pendingFavouriteTeam';

/** One game entry from verifysession/account (games array) */
export interface UserGame {
  name: string;
  values: Record<string, unknown> & {
    last?: string;
    played?: number;
    lastwon?: number;
    lastwon_currency?: string;
    answered?: number;
    questionstoanswer?: number;
  };
}

export interface UserWallet {
  account_id?: number;
  coins: number;
  rubies: number;
  xp?: number;
}

export interface UserFavourites {
  /** Comma-separated favourite sports (e.g. "Cricket, Football"). */
  favsports?: string;
  /** Comma-separated cricketer slugs (profile grid headshots). */
  favcricketers?: string;
  /** Comma-separated IPL franchise slugs (profile picker). */
  faviplteams?: string;
  /** Comma-separated national team slugs (profile picker). */
  favnationalteams?: string;
  /** Comma-separated football club slugs (profile picker). */
  favfootballteams?: string;
  /** Comma-separated cricket team display names starred from match cards/scoreboards. */
  favcustomcricketteams?: string;
}

export interface User {
  id?: string;
  sessionid?: string;
  userId: string | number;
  /** Public shareable id for profile links (e.g. c8_xxxxxxxx). Use for invite and ?p= param. */
  playerId?: string | null;
  created?: string;
  verifychannel: string;
  sent_to: string;
  created_at?: string;
  email: string;
  phone: string;
  country: string;
  last_signin?: string;
  username: string;
  player_name?: string;
  /** Friend-facing real/friendly name (not unique). Null ⇒ fall back to player_name. */
  display_name?: string | null;
  /** Resolved display image URL (preset avatar_url or legacy profile photo). */
  avatar: string;
  /** Original OAuth/social profile photo before preset avatar, when applicable. */
  original_avatar?: string | null;
  avatar_url?: string | null;
  avatar_key?: string | null;
  marketing: boolean;
  min: boolean; // indicates the admin status
  /** When true, user can edit player_name for 1 ruby per change (account page). */
  allow_player_name_edit?: boolean;
  /** One free player_name change (guests get this at creation). */
  free_player_name_change?: boolean;
  /** Legacy guest account that has not signed in with Google / Facebook / SMS yet. */
  is_guest?: boolean;
  /** Set when verifysession returns extended payload (single-request merge with account) */
  games?: UserGame[] | null;
  wallet?: UserWallet | null;
  /** Tournament and team favourites (from account). */
  favourites?: UserFavourites | null;
  /** Who can find your profile: public | friends_of_friends | no_one */
  profile_visibility?: 'public' | 'friends_of_friends' | 'no_one';
  /** When false, other users cannot send you new friend requests. Default true. */
  accept_friend_requests?: boolean;
  /** Privacy JSON (booleans, optional `game_activity_scope`, `career_stats_scope`, etc.). */
  activity_share?: Record<string, unknown>;
  /** False when “hide online” is on or you are not recently active (matches what others see). */
  is_online?: boolean;
  /** Current-week XP podium crown on avatar (gold / silver / bronze). */
  xp_week_crown?: string | null;
  avatar_frame_url?: string | null;
  avatar_frame_small_url?: string | null;
  /** From verifysession: `accounts.vip_until` is in the future. */
  is_vip?: boolean;
}

interface UserContextProps {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  /** True while verifysession is in flight. Do not block the homepage on this—only dev restriction and auth routes (AuthenticatedRoute/ProtectedRoute) should wait on loading. */
  loading: boolean;
}

export const UserContext = createContext<UserContextProps | undefined>(undefined);

const USER_CACHE_KEY = 'c8_user_cache';
const USER_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const PRESENCE_HEARTBEAT_MS = 25 * 1000;
const PRESENCE_TABS_KEY = 'c8_presence_tabs_v1';
const PRESENCE_TAB_STALE_MS = (() => {
  const raw = Number.parseInt(String(process.env.PRESENCE_ONLINE_WINDOW_MS ?? ''), 10);
  return Number.isFinite(raw) && raw > 0 ? raw : 70 * 1000;
})();

function generatePresenceTabId() {
  return `tab_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

function readPresenceTabs(): Record<string, number> {
  try {
    const raw = localStorage.getItem(PRESENCE_TABS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch {
    return {};
  }
}

function writePresenceTabs(next: Record<string, number>) {
  try {
    localStorage.setItem(PRESENCE_TABS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function prunePresenceTabs(tabs: Record<string, number>, now = Date.now()): Record<string, number> {
  const pruned: Record<string, number> = {};
  for (const [tabId, ts] of Object.entries(tabs)) {
    if (typeof ts === 'number' && Number.isFinite(ts) && now - ts <= PRESENCE_TAB_STALE_MS) {
      pruned[tabId] = ts;
    }
  }
  return pruned;
}

function touchPresenceTab(tabId: string): number {
  const now = Date.now();
  const tabs = prunePresenceTabs(readPresenceTabs(), now);
  tabs[tabId] = now;
  writePresenceTabs(tabs);
  return Object.keys(tabs).length;
}

function sendPresenceHeartbeat(): Promise<Response> {
  return fetch(`${process.env.API_URL}/account/presence/heartbeat`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
}

function getCachedUser(): User | null {
  try {
    const raw = sessionStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const { user: cached, ts } = JSON.parse(raw) as { user: User; ts: number };
    if (!cached || !ts || Date.now() - ts > USER_CACHE_TTL_MS) return null;
    return normalizeSessionPayload(cached as Record<string, unknown>) as User;
  } catch {
    return null;
  }
}

function setCachedUser(user: User | null) {
  try {
    if (!user) {
      sessionStorage.removeItem(USER_CACHE_KEY);
      return;
    }
    sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify({ user, ts: Date.now() }));
  } catch {
    // ignore
  }
}

/**
 * Merge favourites into the cached user (session cache) so optimistic changes survive a
 * full-page reload before the next `verifysession` refresh. No-op if there's no cache.
 */
export function patchCachedUserFavourites(favourites: UserFavourites) {
  try {
    const raw = sessionStorage.getItem(USER_CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { user?: User; ts?: number };
    if (!parsed?.user) return;
    parsed.user = {
      ...parsed.user,
      favourites: { ...(parsed.user.favourites ?? {}), ...favourites },
    };
    parsed.ts = Date.now();
    sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(parsed));
  } catch {
    // ignore
  }
}

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const presenceTabId = useMemo(() => generatePresenceTabId(), []);
  const session_token = getSessionToken();
  const jwt = getJwt();
  // Revalidate when auth appears/disappears, not on JWT sliding refresh (new token string).
  const hasJwt = Boolean(jwt);

  useEffect(() => {
    const clearLocalAuthState = () => {
      clearAllClientAuth();
      setUser(null);
      setCachedUser(null);
    };

    if (!session_token && !jwt) {
      setUser(null);
      setLoading(false);
      setCachedUser(null);
      return;
    }

    const cached = getCachedUser();
    if (cached) {
      setUser(normalizeSessionPayload(cached as Record<string, unknown>) as User);
      // Show cached profile immediately, but always revalidate against the server.
      setLoading(false);
    }

    // Treat 401/403 as definitive auth rejection; everything else (network, 5xx, CORS, ad blocker) is transient.
    // Transient failures must NOT clear cookies — the session is still valid and will revalidate next page load.
    const isDefinitiveAuthRejection = (status: number) => status === 401 || status === 403;

    const applySessionPayload = (userData: Record<string, unknown>) => {
      if (isLogoutInProgress()) return;
      applyRefreshedJwtFromResponse(userData);
      const normalized = normalizeSessionPayload(
        stripJwtFromSessionPayload(userData)
      ) as User;
      setUser(normalized);
      setCachedUser(normalized);
      syncAnalyticsIdentityToDataLayer(
        userData.userId != null ? String(userData.userId) : null
      );
      if (userData.userId != null) {
        setUserId(userData.userId);
      }
    };

    const fetchVerify = async (url: string, headers: Record<string, string>) => {
      // One short retry for transient failures (network blip, 5xx) before giving up for this page load.
      const RETRY_DELAYS_MS = [600];
      let lastError: unknown = null;
      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
        try {
          const response = await fetch(url, { method: 'GET', headers });
          if (response.ok || isDefinitiveAuthRejection(response.status)) {
            return { ok: true as const, response };
          }
          lastError = new Error(`verifysession returned ${response.status}`);
        } catch (err) {
          lastError = err;
        }
        const delay = RETRY_DELAYS_MS[attempt];
        if (delay != null) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
      return { ok: false as const, error: lastError };
    };

    const checkSession = async (background = false) => {
      if (!background && !cached) {
        setLoading(true);
      }
      try {
        const url = getVerifySessionUrl();
        if (!url) {
          // No usable cookies at all — user is genuinely signed out.
          clearLocalAuthState();
          return;
        }

        const result = await fetchVerify(url, getAuthHeaders());
        if (!result.ok) {
          // Transient failure (network/5xx/CORS) — keep cookies, leave cached user as-is.
          // Next page load or next auth-touching action will revalidate. Never log the user out here.
          if (process.env.NODE_ENV !== 'production') {
            const message =
              result.error instanceof Error ? result.error.message : String(result.error);
            console.warn('verifysession transient failure (keeping session):', message);
          }
          return;
        }

        const { response } = result;
        if (response.ok) {
          const userData = (await response.json()) as Record<string, unknown>;
          applySessionPayload(userData);
          return;
        }

        if (await consumeAccountDeletedIfPresent(response)) {
          // Account was deleted server-side — definitive logout.
          clearLocalAuthState();
          return;
        }

        // 401/403 on JWT-first: try the legacy session-path fallback before giving up.
        if (
          jwt &&
          session_token &&
          response.status === 401 &&
          process.env.API_URL
        ) {
          const sessionUrl = `${process.env.API_URL}/verifysession/${encodeURIComponent(session_token)}`;
          const fallback = await fetchVerify(sessionUrl, { 'Content-Type': 'application/json' });
          if (!fallback.ok) {
            // Fallback hit a transient failure — keep cookies and try again next page load.
            return;
          }
          if (fallback.response.ok) {
            const userData = (await fallback.response.json()) as Record<string, unknown>;
            applySessionPayload(userData);
            return;
          }
          if (await consumeAccountDeletedIfPresent(fallback.response)) {
            clearLocalAuthState();
            return;
          }
          if (isDefinitiveAuthRejection(fallback.response.status)) {
            // Both JWT-first and session-fallback returned definitive auth rejection — really signed out.
            clearLocalAuthState();
          }
          return;
        }

        // Definitive 401/403 on the only path we have (no fallback available) — signed out.
        clearLocalAuthState();
      } catch (err) {
        // Defensive: any unexpected throw above (e.g. malformed JSON) — treat as transient,
        // do NOT clear cookies. Worst case the user appears signed out for this page; refresh recovers.
        if (process.env.NODE_ENV !== 'production') {
          const message = err instanceof Error ? err.message : String(err);
          console.error('Unexpected error during session check (keeping session):', message);
        }
      } finally {
        if (!background || !cached) {
          setLoading(false);
        }
      }
    };

    checkSession(Boolean(cached));
  }, [session_token, hasJwt]);

  useEffect(() => {
    const onForceLogout = () => {
      setUser(null);
      setCachedUser(null);
    };
    // Cross-tab logout: when auth is cleared in another tab (localStorage removal fires a
    // `storage` event only in *other* tabs), drop the in-memory user here too.
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== 'c8_session_token' && event.key !== 'c8_jwt') {
        return;
      }
      if (!getSessionToken() && !getJwt()) {
        setUser(null);
        setCachedUser(null);
      }
    };
    window.addEventListener(ACCOUNT_DELETED_LOGOUT_EVENT, onForceLogout);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(ACCOUNT_DELETED_LOGOUT_EVENT, onForceLogout);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setUserAndCache = useCallback<React.Dispatch<React.SetStateAction<User | null>>>(
    (value) => {
      setUser((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        setCachedUser(next);
        return next;
      });
    },
    []
  );

  const contextValue = useMemo(
    () => ({ user, setUser: setUserAndCache, loading }),
    [user, loading, setUserAndCache]
  );

  const contextLoadedFiredRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (user?.userId != null && String(user.userId) !== '') {
      const uid = String(user.userId);
      syncAnalyticsIdentityToDataLayer(uid);
      if (contextLoadedFiredRef.current !== uid) {
        contextLoadedFiredRef.current = uid;
        trackUserContextLoaded({ user_id: uid });
      }
      return;
    }

    contextLoadedFiredRef.current = null;
    // Do not push guest while session verify may still confirm a cookie-backed login.
    // (seedAnalyticsIdentityBeforeGtm already set logged_in from auth cookies.)
    const stillHasAuth =
      Boolean(getJwt()) ||
      Boolean(getSessionToken()) ||
      Boolean(getUserId());
    if (!stillHasAuth) {
      syncAnalyticsIdentityToDataLayer(null);
    }
  }, [user]);

  useEffect(() => {
    if (user?.userId == null || String(user.userId) === '') return;
    if (!session_token && !jwt) return;
    void tryClaimSquaresLinkInviteFromStorage(user.userId);
    void tryClaimBullionLinkInviteFromStorage(user.userId);
    void tryClaimPendingFavouriteTeamFromStorage().then((favs) => {
      if (!favs) return;
      setUserAndCache((prev) => {
        if (!prev) return prev;
        return { ...prev, favourites: { ...(prev.favourites ?? {}), ...favs } };
      });
    });
  }, [user?.userId, session_token, jwt, setUserAndCache]);

  useEffect(() => {
    if (!user?.userId) return;
    const heartbeat = () => {
      touchPresenceTab(presenceTabId);
      sendPresenceHeartbeat()
        .then((res) => {
          if (!res.ok) return;
          setUserAndCache((prev) => {
            if (!prev || !allowsOnlinePresenceShare(prev.activity_share as Record<string, unknown> | undefined)) {
              return prev;
            }
            if (prev.is_online === true) return prev;
            return { ...prev, is_online: true };
          });
        })
        .catch(() => {});
    };

    heartbeat();

    const heartbeatId = window.setInterval(() => {
      heartbeat();
    }, PRESENCE_HEARTBEAT_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        heartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(heartbeatId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user?.userId, presenceTabId, setUserAndCache]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
